
import React, { useEffect, useState } from 'react';
import {
    Plus,
    Minus,
    ArrowRight,
    Zap,
    ShieldCheck,
    TrendingUp,
    Info,
    ChevronRight,
    RefreshCw,
    Coins,
    MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useCryptoPrices } from '@/shared/hooks/useCryptoPrices';
import { useAnnouncements } from '@/shared/hooks/useAnnouncements';
import { useAuth } from '@/shared/hooks/useAuth';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { assets } = useUserAssets(user?.uid);
    const { announcements } = useAnnouncements(true);
    const [currentSymbol, setCurrentSymbol] = useState('BTC');
    const { prices: livePrices, loading: pricesLoading } = useCryptoPrices();

    const formatMoney = (value: number | undefined) => {
        if (value === undefined || value === null) return '0';
        return value.toLocaleString();
    };

    // Calculate total estimated value (Only KRW, no minus)
    const totalEstimatedValue = Math.max(0, assets?.krw ?? 0);

    const cryptoPrices = [
        { symbol: 'BTC', name: 'Bitcoin', iconColor: 'bg-orange-500 shadow-orange-500/20' },
        { symbol: 'ETH', name: 'Ethereum', iconColor: 'bg-indigo-500 shadow-indigo-500/20' },
        { symbol: 'USDT', name: 'Tether', iconColor: 'bg-emerald-500 shadow-emerald-500/20' }
    ];

    // TradingView Widget
    useEffect(() => {
        const containerId = 'tradingview_widget_home';
        const container = document.getElementById(containerId);
        if (!container) return;
        container.innerHTML = '';

        const symbolMap: Record<string, string> = {
            'BTC': 'UPBIT:BTCKRW',
            'ETH': 'UPBIT:ETHKRW',
            'USDT': 'UPBIT:USDTKRW'
        };

        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
            if ((window as any).TradingView) {
                new (window as any).TradingView.widget({
                    container_id: containerId,
                    symbol: symbolMap[currentSymbol] || 'BINANCE:BTCUSDT',
                    theme: 'dark',
                    style: '1',
                    locale: 'kr',
                    toolbar_bg: '#131722',
                    enable_publishing: false,
                    allow_symbol_change: true,
                    hide_top_toolbar: false,
                    hide_legend: false,
                    save_image: false,
                    autosize: true,
                    interval: '30',
                    timezone: 'Asia/Seoul'
                });
            }
        };
        document.head.appendChild(script);

        return () => {
            try { document.head.removeChild(script); } catch (e) { }
        };
    }, [currentSymbol]);

    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-bottom-5 duration-1000 text-foreground pb-24">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 px-4 lg:px-0">
                <div className="space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                        <Zap className="h-3.5 w-3.5 fill-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">글로벌 자산 관리</span>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                        안녕하세요, {assets?.name || user?.email?.split('@')[0] || '회원'}님 👋<br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                            프리미엄 자산 관리의 시작
                        </span>
                    </h1>
                    <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                        보다 안전하고 빠른 거래를 경험해 보세요.<br />
                        코인투캐쉬만의 에스크로 기반 시스템이 당신의 자산을 완벽하게 보호합니다.
                    </p>
                </div>

                {announcements.length > 0 && (
                    <div
                        onClick={() => navigate('/notices')}
                        className="flex items-center gap-4 p-5 rounded-[2rem] bg-card border border-border hover:border-blue-500/30 transition-all cursor-pointer group max-w-sm w-full shadow-2xl relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 blur-2xl rounded-full"></div>
                        <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-lg shadow-blue-600/20 relative z-10">
                            <Info className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0 relative z-10">
                            <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground mb-0.5">최신 공지사항</p>
                            <p className="text-xs font-bold text-foreground truncate group-hover:text-blue-600 transition-colors uppercase">
                                {announcements[0].title}
                            </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground/30 group-hover:translate-x-1 transition-transform relative z-10" />
                    </div>
                )}
            </div>


            {/* Unified Market & Chart Section */}
            <div className="rounded-[2rem] md:rounded-[3rem] bg-card border border-border shadow-2xl overflow-hidden relative group max-w-[100vw]">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/[0.02] blur-[150px] rounded-full -mr-80 -mt-80 pointer-events-none transition-colors group-hover:bg-blue-600/[0.04]"></div>

                {/* Market Header with Coin Selector */}
                <div className="relative z-10 p-4 md:p-8 pb-0">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-blue-600/10 border border-blue-500/20">
                                <TrendingUp className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-xl md:text-2xl font-black tracking-tighter text-foreground uppercase">실시간 마켓</h2>
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em]">글로벌 기초 자산 시세 · Live Chart</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-emerald-500 uppercase animate-pulse flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                Live
                            </span>
                        </div>
                    </div>

                    {/* Coin Cards - Responsive Flex Wrap */}
                    <div className="flex flex-wrap gap-4 mb-6">
                        {cryptoPrices.map((coin) => (
                            <div
                                key={coin.symbol}
                                onClick={() => setCurrentSymbol(coin.symbol)}
                                className={cn(
                                    "p-4 md:p-5 rounded-2xl transition-all cursor-pointer group/coin border-2 relative overflow-hidden flex-1 min-w-[140px] md:min-w-[200px]",
                                    currentSymbol === coin.symbol
                                        ? "bg-blue-600/10 border-blue-500/30 shadow-lg shadow-blue-500/10"
                                        : "bg-accent/30 border-transparent hover:border-border hover:bg-accent/50"
                                )}
                            >
                                {currentSymbol === coin.symbol && (
                                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600"></div>
                                )}
                                <div className="flex items-center gap-3 md:gap-4">
                                    <div className={cn("w-9 h-9 md:w-11 md:h-11 rounded-xl flex items-center justify-center text-white text-xs md:text-sm font-black shadow-lg group-hover/coin:scale-110 transition-transform duration-300 shrink-0", coin.iconColor)}>
                                        {coin.symbol[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-black text-sm md:text-base text-foreground uppercase tracking-tight">{coin.symbol}</h4>
                                            <span className="text-[10px] text-muted-foreground font-bold italic hidden md:inline">{coin.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <p className="font-black text-xs md:text-sm text-foreground tabular-nums">₩{(livePrices[coin.symbol]?.price || 0).toLocaleString()}</p>
                                            <p className={cn("text-[10px] font-black", (livePrices[coin.symbol]?.change || 0) >= 0 ? "text-emerald-500" : "text-rose-500")}>
                                                {(livePrices[coin.symbol]?.change || 0) >= 0 ? '▲' : '▼'} {(livePrices[coin.symbol]?.change || 0).toFixed(2)}%
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* TradingView Chart */}
                <div className="relative z-10 p-4 md:px-8 md:pb-8 w-full">
                    <div className="rounded-2xl border border-border overflow-hidden bg-[#131722] w-full" style={{ height: '500px' }}>
                        <div id="tradingview_widget_home" className="h-full w-full" />
                    </div>
                </div>
            </div>

            {/* Quick Support FAB */}
            <div className="fixed bottom-24 right-6 lg:bottom-10 lg:right-10 z-[100]">
                <button
                    onClick={() => navigate('/support')}
                    className="group relative flex items-center justify-center"
                >
                    <div className="absolute inset-0 bg-blue-600 blur-xl opacity-40 group-hover:opacity-60 transition-opacity rounded-full"></div>
                    <div className="relative w-14 h-14 lg:w-16 lg:h-16 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 border border-white/20">
                        <MessageSquare className="h-6 w-6 lg:h-7 lg:w-7" />
                        <span className="absolute right-full mr-4 bg-card border border-border px-4 py-2 rounded-xl text-xs font-black text-foreground shadow-2xl opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all pointer-events-none whitespace-nowrap uppercase tracking-widest hidden md:block">
                            실시간 고객센터 문의
                        </span>
                    </div>
                </button>
            </div>
        </div>
    );
};

export default HomePage;
